import { Heading } from "@/components/heading"
import { RaceShield } from "@/components/race-shield"
import { Rivets } from "@/components/rivets"
import { Card, CardContent } from "@/components/ui/card"
import { VsDiamond } from "@/components/vs-diamond"
import type { MatchPageProps } from "@/types"

export function InProgressState({
  match,
  lineupSize,
}: {
  match: MatchPageProps["match"]
  lineupSize: number | null
}) {
  return (
    <div className="py-16 text-center">
      {/* Mini scoreboard */}
      <div
        className="relative mx-auto mb-8 max-w-lg p-6"
        style={{
          background:
            "linear-gradient(180deg, oklch(0.18 0.02 50), oklch(0.14 0.01 50))",
          border: "3px solid var(--border-heavy)",
          boxShadow:
            "inset 2px 2px 0 oklch(0.25 0.02 70 / 0.3), inset -2px -2px 0 oklch(0.08 0.01 50 / 0.6), 0 4px 16px oklch(0 0 0 / 0.5)",
        }}
      >
        {/* Scoreboard rivets */}
        <Rivets />
        <div className="flex items-center justify-center gap-8">
          <div className="flex-1 text-center">
            <RaceShield
              race={match.home_team.race}
              hue={match.home_team.color_hue ?? undefined}
              size="md"
              className="mx-auto mb-2"
            />
            <h3 className="font-display text-gold-sm text-lg leading-none font-bold">
              {match.home_team.name}
            </h3>
          </div>
          <VsDiamond />
          <div className="flex-1 text-center">
            <RaceShield
              race={match.away_team.race}
              hue={match.away_team.color_hue ?? undefined}
              size="md"
              className="mx-auto mb-2"
            />
            <h3
              className="font-display text-lg leading-none font-bold"
              style={{ color: "var(--foreground)" }}
            >
              {match.away_team.name}
            </h3>
          </div>
        </div>
      </div>

      {/* Simulation indicator */}
      <div className="animate-sim-pulse mb-4">
        <svg
          className="mx-auto size-12"
          style={{ color: "var(--accent)" }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polygon
            points="10 8 16 12 10 16 10 8"
            fill="currentColor"
            stroke="none"
          />
        </svg>
      </div>

      <Heading level={1} wrapperClassName="mb-2" className="text-3xl">
        The Match is Underway
      </Heading>
      <p className="text-lg" style={{ color: "var(--muted-foreground)" }}>
        Simulating
        <span className="animate-sim-dot">.</span>
        <span className="animate-sim-dot" style={{ animationDelay: "0.3s" }}>
          .
        </span>
        <span className="animate-sim-dot" style={{ animationDelay: "0.6s" }}>
          .
        </span>
      </p>
      <p className="mt-4 text-sm" style={{ color: "var(--muted-foreground)" }}>
        Your lineup has been locked in. Results will appear here when the
        simulation is complete.
      </p>

      <div className="mt-8">
        <Card className="mx-auto max-w-sm py-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <svg
                className="size-5 flex-shrink-0"
                style={{ color: "var(--accent-dim)" }}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <div className="text-left">
                <p
                  className="text-xs font-bold tracking-widest uppercase"
                  style={{ color: "var(--accent-dim)" }}
                >
                  Lineup Confirmed
                </p>
                <p
                  className="text-sm"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  {lineupSize !== null
                    ? `${lineupSize} players fielded`
                    : "Match simulation in progress"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
